import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table } from "components/base";
import { TableProps } from "antd/lib/table";
import { Grid, Card, Row, Col, Typography, Empty, Skeleton, Tag, Dropdown, Menu, Button } from "antd";
import { EnvironmentOutlined, MoreOutlined } from "@ant-design/icons";

import { AppState } from "../../../store";
import { initializeClients, setBulkActionsClients } from "store/actions";
import { parseOrganizationClients } from "../../../utils/parsers";
import { ActionMenu } from "./cell-renders";

import "styles/components/tab-content/client-content/client-content.scss";

const { useBreakpoint } = Grid;
const { Text, Title } = Typography;

// Desktop/Tablet Table Columns
const getColumns = (isTablet: boolean): TableProps<any>["columns"] => {
  const columns: TableProps<any>["columns"] = [
    {
      title: "Name",
      dataIndex: "name",
      width: isTablet ? 140 : 170,
      ellipsis: true,
      fixed: isTablet ? "left" : undefined,
    },
    {
      title: "Address",
      dataIndex: "address",
      width: isTablet ? 120 : 130,
      ellipsis: true,
      responsive: ["md"],
    },
    {
      title: "City",
      dataIndex: "city",
      width: isTablet ? 100 : 130,
      ellipsis: true,
      responsive: ["lg"],
    },
    {
      title: "Province",
      dataIndex: "state",
      width: isTablet ? 100 : 130,
      ellipsis: true,
      responsive: ["lg"],
    },
    {
      title: "Country",
      dataIndex: "country",
      width: isTablet ? 100 : 130,
      ellipsis: true,
      responsive: ["md"],
    },
    {
      title: "",
      dataIndex: "action",
      render: ActionMenu,
      align: "right",
      width: isTablet ? 50 : 56,
      fixed: "right",
      className: "action-menu open-link --issues",
    },
  ];

  return columns;
};

// Organization Card Component for Mobile
interface OrganizationCardProps {
  organization: any;
}

const OrganizationCard = ({ organization }: OrganizationCardProps) => {
  return (
    <Card className="organization-card" size="small">
      {/* Action Button - Top Right Corner */}
      <div className="card-action-corner" onClick={(e) => e.stopPropagation()}>
            {ActionMenu("", organization)}
          </div>
      {/* Card Content */}
      <div className="card-content">
        {/* Organization Name */}
        <div className="org-header">
          <Title level={5} className="org-name">
            {organization.name || "Unnamed Organization"}
          </Title>
        </div>

        {/* Address Section */}
        <div className="address-section">
          <div className="address-icon">
            <EnvironmentOutlined />
          </div>
          <div className="address-details">
            {organization.address && (
              <Text className="address-line">{organization.address}</Text>
            )}
            <Text className="location-line">
              {[organization.city, organization.state, organization.country]
                .filter(Boolean)
                .join(", ") || "Location not specified"}
            </Text>
          </div>
        </div>

        {/* Tags/Stats Section (Optional) */}
        {(organization.vehicle_count || organization.organization_type) && (
          <div className="card-footer">
            {organization.vehicle_count && (
              <Tag color="blue">{organization.vehicle_count} Vehicles</Tag>
            )}
            {organization.organization_type && (
              <Tag color="green">{organization.organization_type}</Tag>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

// Loading Skeleton for Mobile
const MobileSkeleton = () => (
  <Row gutter={[12, 12]}>
    {[1, 2, 3, 4].map((item) => (
      <Col xs={24} sm={12} key={item}>
        <Card className="organization-card">
          <Skeleton active paragraph={{ rows: 2 }} />
        </Card>
      </Col>
    ))}
  </Row>
);

// Mobile List View
interface MobileViewProps {
  data: any[];
  loading?: boolean;
}

const MobileView = ({ data, loading }: MobileViewProps) => {
  if (loading) {
    return <MobileSkeleton />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="empty-state">
        <Empty
          description="No organizations found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div className="mobile-organizations-list">
      <Row gutter={[12, 12]}>
        {data.map((org, index) => (
          <Col xs={24} sm={12} key={org.id || org.key || index}>
            <OrganizationCard organization={org} />
          </Col>
        ))}
      </Row>
    </div>
  );
};

// Main TableView Component
export default function TableView() {
  const dispatch = useDispatch();
  const screens = useBreakpoint();

  const isMobile = !screens.md;
  const isTablet = !!(screens.md && !screens.lg);

  const organizationsState = useSelector((state: AppState) => state.organizations);
  const {
    organizations,
    page,
    search,
    filters: { listType },
  } = organizationsState;
  const loading = (organizationsState as any).loading ?? false;

  const data = useMemo(
    () => parseOrganizationClients(search, organizations),
    [organizations, listType, search]
  );

  useEffect(() => {
    dispatch(initializeClients(1, listType));
  }, [dispatch, listType]);

  const columns = useMemo(() => getColumns(isTablet), [isTablet]);

  const getScrollConfig = () => {
    if (isTablet) {
      return { y: "65vh", x: 500 };
    }
    return { y: "70vh" };
  };

  return (
    <div className="organizations-table-container">
      {isMobile ? (
        <MobileView data={data} loading={loading} />
      ) : (
        <Table
          className="organizations-table"
          columns={columns}
          dataSource={data}
          pagination={false}
       //   scroll={getScrollConfig()}
          size={isTablet ? "small" : "middle"}
          rowKey={(record) => record.id || record.key}
        />
      )}
    </div>
  );
}